class PdpSwiperGalleryZoom {
  constructor(root) {
    this.root = root;
    this.mode = root.dataset.imageZoom;
    this.zoomRatio = 2;
    this.lightbox = root.querySelector('[data-pdp-gallery-lightbox]');
    this.lightboxContent = root.querySelector('[data-pdp-gallery-lightbox-content]');
    this._bound = false;
  }

  init() {
    if (this.mode === 'none' || this._bound) return;
    this._bound = true;

    this.root.addEventListener('click', this.onClick);
    this.lightbox?.addEventListener('pointerup', this.onLightboxPointerUp);
    this.lightbox?.addEventListener('cancel', this.closeLightbox);
    this.lightbox?.querySelector('[data-pdp-zoom-close]')?.addEventListener('click', this.onCloseClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  destroy() {
    if (!this._bound) return;
    this._bound = false;
    this.root.removeEventListener('click', this.onClick);
    this.lightbox?.removeEventListener('pointerup', this.onLightboxPointerUp);
    this.lightbox?.removeEventListener('cancel', this.closeLightbox);
    this.lightbox?.querySelector('[data-pdp-zoom-close]')?.removeEventListener('click', this.onCloseClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.closeLightbox();
  }

  onClick = (event) => {
    const trigger = event.target.closest('[data-pdp-zoom-trigger]');
    const image = event.target.closest('.pdp-swiper-gallery__image[data-zoom-src]');

    if (this.mode === 'lightbox') {
      if (trigger || (image && !event.target.closest('.pdp-swiper-gallery__thumb'))) {
        event.preventDefault();
        const targetImage = trigger
          ? trigger.closest('.pdp-swiper-gallery__main-slide')?.querySelector('.pdp-swiper-gallery__image[data-zoom-src]')
          : image;
        if (targetImage) this.openLightbox(targetImage);
      }
      return;
    }

    if (this.mode === 'hover') {
      const hoverImage = trigger
        ? trigger.closest('.pdp-swiper-gallery__main-slide')?.querySelector('.pdp-swiper-gallery__image--zoom-hover')
        : image?.classList.contains('pdp-swiper-gallery__image--zoom-hover')
          ? image
          : null;

      if (!hoverImage) return;
      event.preventDefault();
      this.magnify(hoverImage, event);
    }
  };

  onCloseClick = (event) => {
    event.preventDefault();
    this.closeLightbox();
  };

  onLightboxPointerUp = (event) => {
    if (!this.lightbox?.open) return;
    if (event.target.closest('[data-pdp-zoom-close]')) return;
    if (event.target.closest('.pdp-swiper-gallery__lightbox-img')) return;
    this.closeLightbox();
  };

  isMobileLightbox() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  getLightboxImageSrc(sourceImg) {
    if (this.isMobileLightbox()) {
      return sourceImg.currentSrc || sourceImg.src;
    }

    return sourceImg.dataset.zoomSrc || sourceImg.currentSrc || sourceImg.src;
  }

  syncLightboxFromGallery() {
    if (!this.lightboxContent) return;

    const slides = this.root.querySelectorAll(
      '[data-pdp-gallery-main] > .pdp-swiper-gallery__main-slide:not(.swiper-slide-duplicate)'
    );

    this.lightboxContent.innerHTML = '';

    slides.forEach((slide) => {
      const sourceImg = slide.querySelector('.pdp-swiper-gallery__image[data-zoom-src]');
      if (!sourceImg) return;

      const item = document.createElement('div');
      item.className = 'pdp-swiper-gallery__lightbox-item';
      item.dataset.lightboxMediaId = slide.dataset.mediaId;

      const img = document.createElement('img');
      img.className = 'pdp-swiper-gallery__lightbox-img';
      img.alt = sourceImg.alt || '';
      img.loading = 'lazy';
      img.src = this.getLightboxImageSrc(sourceImg);

      item.appendChild(img);
      this.lightboxContent.appendChild(item);
    });
  }

  onKeydown = (event) => {
    if (!this.lightbox?.open || event.key !== 'Escape') return;
    event.preventDefault();
    this.closeLightbox();
  };

  openLightbox(image) {
    const slide = image.closest('[data-media-id]');
    const mediaId = slide?.dataset.mediaId;
    if (!this.lightbox || !this.lightboxContent) return;

    this.syncLightboxFromGallery();

    const items = this.lightboxContent.querySelectorAll('[data-lightbox-media-id]');
    const activeItem = mediaId
      ? this.lightboxContent.querySelector(`[data-lightbox-media-id="${mediaId}"]`)
      : null;

    items.forEach((item) => {
      item.classList.toggle('is-active', item === activeItem);
    });

    this.lightbox.showModal();
    document.body.classList.add('pdp-swiper-gallery--lightbox-open');

    if (!activeItem) return;

    requestAnimationFrame(() => {
      activeItem.scrollIntoView({ block: 'center' });
      this.centerLightboxItem(activeItem);
    });
  }

  centerLightboxItem(item) {
    const img = item?.querySelector('img');
    if (!this.lightboxContent || !img) return;
    this.lightboxContent.scrollLeft = Math.max(0, (img.offsetWidth - this.lightboxContent.clientWidth) / 2);
  }

  closeLightbox = () => {
    if (!this.lightbox?.open) return;
    this.lightbox.close();
    document.body.classList.remove('pdp-swiper-gallery--lightbox-open');
  };

  magnify(image, event) {
    const existing = image.parentElement?.querySelector('.pdp-swiper-gallery__magnify-overlay');
    if (existing) {
      existing.remove();
      return;
    }

    const fullSrc = image.dataset.zoomSrc || image.currentSrc || image.src;
    const overlay = document.createElement('div');
    overlay.className = 'pdp-swiper-gallery__magnify-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.backgroundImage = `url('${fullSrc}')`;

    const preload = new Image();
    image.style.opacity = '0.5';

    preload.onload = () => {
      image.style.opacity = '1';
      image.parentElement?.insertBefore(overlay, image);
      this.moveMagnifyOverlay(image, overlay, event);
    };
    preload.src = fullSrc;

    overlay.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('mousemove', (moveEvent) => this.moveMagnifyOverlay(image, overlay, moveEvent));
    overlay.addEventListener('mouseleave', () => overlay.remove());
  }

  moveMagnifyOverlay(image, overlay, event) {
    const ratio = image.height / image.width;
    const container = image.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    overlay.style.backgroundSize = `${image.width * this.zoomRatio}px`;
  }
}

function getPdpSwiperGalleryZoom(root) {
  if (!root || root.dataset.imageZoom === 'none') return null;

  if (!root._pdpSwiperGalleryZoom) {
    root._pdpSwiperGalleryZoom = new PdpSwiperGalleryZoom(root);
    root._pdpSwiperGalleryZoom.init();
  }

  return root._pdpSwiperGalleryZoom;
}

function initPdpSwiperGalleryZoom(scope = document) {
  scope
    .querySelectorAll('[data-pdp-swiper-gallery][data-image-zoom]:not([data-image-zoom="none"])')
    .forEach(getPdpSwiperGalleryZoom);
}

window.PdpSwiperGalleryZoom = {
  get: getPdpSwiperGalleryZoom,
  init: initPdpSwiperGalleryZoom,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPdpSwiperGalleryZoom());
} else {
  initPdpSwiperGalleryZoom();
}
